import React, { useMemo, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { FiX, FiCalendar, FiClock, FiRefreshCw, FiAlertCircle } from "react-icons/fi";
import { API_IP } from "../../global_variables";
import { axiosReqConfig } from "../../functions/axiosRequest";
import "./RescheduleModal.css";

const RESCHEDULE_CUTOFF_HOURS = 24;

function pad(n) {
  return n.toString().padStart(2, "0");
}

function toDateInputValue(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function computeMinDateTime() {
  const t = new Date();
  t.setHours(t.getHours() + RESCHEDULE_CUTOFF_HOURS);
  return t;
}

function parseHours(startTime, endTime) {
  if (!startTime || !endTime) return null;
  const toHours = (s) => {
    const [h, m] = s.split(":").map((v) => parseInt(v, 10) || 0);
    return h + m / 60;
  };
  const diff = toHours(endTime) - toHours(startTime);
  return diff > 0 ? diff : null;
}

export default function RescheduleModal({ open, onClose, eventDetail, onSuccess }) {
  const minDateTime = useMemo(computeMinDateTime, [open]);
  const minDate = toDateInputValue(minDateTime);
  const [newDate, setNewDate] = useState(minDate);
  const [newTime, setNewTime] = useState("");
  const [reason, setReason] = useState("");
  const [providerChoice, setProviderChoice] = useState("same_provider");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setNewDate(minDate);
      setNewTime(eventDetail?.start_time?.slice(0, 5) || "");
      setReason("");
      setProviderChoice("same_provider");
      setError("");
    }
  }, [open, minDate, eventDetail]);

  if (!open) return null;

  const hasScheduleDateId = !!eventDetail?.schedule_date_id;
  const hours = parseHours(eventDetail?.start_time, eventDetail?.end_time) ?? Number(eventDetail?.hours);
  const cutoffLabel = minDateTime.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });

  async function submit(e) {
    e?.preventDefault?.();
    setError("");

    if (!newDate || !newTime) {
      setError("Please choose both a new date and a start time.");
      return;
    }
    if (!hours || Number.isNaN(hours)) {
      setError("Cannot determine visit length. Reload and try again.");
      return;
    }
    if (!hasScheduleDateId) {
      setError("Missing schedule reference on this appointment. Contact support to reschedule.");
      return;
    }
    const proposed = new Date(`${newDate}T${newTime}`);
    if (proposed < minDateTime) {
      setError(`New time must be after ${cutoffLabel} (24-hour notice).`);
      return;
    }

    setSubmitting(true);
    try {
      const res = await axiosReqConfig(`${API_IP}/api/reschedule`, "post", {
        booking_id: eventDetail.fk_booking_id,
        scope: "single_day",
        provider_choice: providerChoice,
        reason: reason.trim() || null,
        items: [
          {
            schedule_date_id: eventDetail.schedule_date_id,
            new_date: newDate,
            new_start_time: `${newTime}:00`,
            new_hours: hours,
          },
        ],
      });
      if (res?.ok) {
        onSuccess?.(res.data);
        onClose();
      } else {
        setError(res?.data || "Could not submit reschedule request.");
      }
    } catch (err) {
      setError(err?.response?.data || err?.message || "Network error.");
    } finally {
      setSubmitting(false);
    }
  }

  return createPortal(
    <div
      className="RM-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="RM-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="RM-panel">
        <header className="RM-header">
          <div className="RM-header-title">
            <FiRefreshCw className="RM-header-icon" aria-hidden="true" />
            <h2 id="RM-title">Reschedule appointment</h2>
          </div>
          <button
            type="button"
            className="RM-close"
            onClick={onClose}
            aria-label="Close reschedule dialog"
          >
            <FiX />
          </button>
        </header>

        <section className="RM-current" aria-label="Current appointment">
          <p className="RM-current-label">Current</p>
          <p className="RM-current-value">
            <FiCalendar aria-hidden="true" />
            <span>{eventDetail?.date_day || "—"}</span>
            <span className="RM-dot" aria-hidden="true">·</span>
            <FiClock aria-hidden="true" />
            <span>
              {eventDetail?.start_time?.slice(0, 5)}
              {eventDetail?.end_time ? ` – ${eventDetail.end_time.slice(0, 5)}` : ""}
            </span>
          </p>
          <p className="RM-current-service">
            {eventDetail?.care_service_name}
            {eventDetail?.provider_name ? ` with ${eventDetail.provider_name}` : ""}
          </p>
        </section>

        <form className="RM-form" onSubmit={submit}>
          <div className="RM-row RM-two">
            <label className="RM-field">
              <span className="RM-field-label">New date</span>
              <input
                type="date"
                min={minDate}
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                required
                aria-required="true"
              />
            </label>
            <label className="RM-field">
              <span className="RM-field-label">Start time</span>
              <input
                type="time"
                value={newTime}
                onChange={(e) => setNewTime(e.target.value)}
                required
                aria-required="true"
              />
            </label>
          </div>

          <fieldset className="RM-choice">
            <legend className="RM-field-label">Provider</legend>
            <label className="RM-radio">
              <input
                type="radio"
                name="providerChoice"
                value="same_provider"
                checked={providerChoice === "same_provider"}
                onChange={() => setProviderChoice("same_provider")}
              />
              <span>Keep same provider</span>
              <small>They must accept the new time.</small>
            </label>
            <label className="RM-radio">
              <input
                type="radio"
                name="providerChoice"
                value="open_pool"
                checked={providerChoice === "open_pool"}
                onChange={() => setProviderChoice("open_pool")}
              />
              <span>Open to any qualified provider</span>
              <small>First available provider claims it.</small>
            </label>
          </fieldset>

          <label className="RM-field">
            <span className="RM-field-label">
              Reason <span className="RM-optional">(optional)</span>
            </span>
            <textarea
              rows={3}
              value={reason}
              maxLength={500}
              placeholder="Anything the provider should know"
              onChange={(e) => setReason(e.target.value)}
            />
          </label>

          <p className="RM-hint">
            Reschedules must be made at least 24 hours before the visit —
            no earlier than <strong>{cutoffLabel}</strong>.
          </p>

          {error && (
            <div className="RM-error" role="alert" aria-live="assertive">
              <FiAlertCircle aria-hidden="true" />
              <span>{typeof error === "string" ? error : "Could not submit reschedule."}</span>
            </div>
          )}

          <div className="RM-footer">
            <button
              type="button"
              className="RM-btn RM-btn-ghost"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="RM-btn RM-btn-primary"
              disabled={submitting}
              aria-busy={submitting || undefined}
            >
              {submitting ? "Submitting…" : "Request reschedule"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
